<template>
  <section class="refer-a-friend">
    <div class="row image-copy-split">
      <div class="columns large-6 col-copy">
        <div class="constrain-width">
          <template v-if="submitted">
            <h1 v-html="campaignContent.thank_you_page_header_text" />
            <p v-html="campaignContent.thank_you_page_blurb_text" />
          </template>
          <template v-else>
            <h1 v-html="campaignContent.landing_header_text" />
            <h2
              v-if="campaignContent.landing_subheader_text"
              v-html="campaignContent.landing_subheader_text"
            />
            <p v-html="campaignContent.landing_blurb_text" />
          </template>
          <fieldset>
            <legend>Share your link anywhere:</legend>

            <div v-if="invitePURL" class="refer-link">
              <label class="sr-only" for="shareableLink">Shareable Link</label>
              <input
                id="shareableLink"
                type="text"
                :value="invitePURL"
                disabled
              />
              <button @click="copyPURL">Copy</button>
            </div>
          </fieldset>

          <fieldset v-if="!submitted">
            <legend>Send an email:</legend>
            <FormKit
              v-model="formData"
              type="form"
              :actions="false"
              @submit="submit"
            >
              <label class="sr-only" for="referEmail"
                >Your friend's email address</label
              >
              <FormKit
                id="referEmail"
                type="email"
                name="email"
                validation="required|email"
                :validation-messages="{
                  required: 'Error: Email address is required.',
                  email: 'Error: Please enter a valid email address.',
                }"
                placeholder="Enter your friend's email address"
                @blur="clearFormErrors"
              />
              <label class="sr-only" for="referMessage">Message</label>
              <FormKit
                id="referMessage"
                type="textarea"
                name="content"
                placeholder="Message"
              />
              <Error v-for="error in formErrors" :key="error">
                {{ error }}
              </Error>
              <FormKit
                type="submit"
                :disabled="Object.keys(inputErrors).length > 0"
                :classes="{
                  outer: 'button black',
                }"
                :label="campaignContent.landing_page_cta_button_text"
              />
            </FormKit>
          </fieldset>
          <div v-else>
            <br />
            <a href="#" class="button black" @click.prevent="resetForm"
              >Share Again</a
            >
          </div>
          <div class="constrain-width" style="text-align: left">
            <br />
            <a class="link" href="/terms/">Terms &amp; Conditions</a>
          </div>
        </div>
      </div>
      <div class="columns large-6 image-col"></div>
    </div>
  </section>
</template>

<script setup>
import Error from '#common/components/Error/Error.vue'
import { pushEvent } from '#common/utils/user'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const metadata = useMetadata()
const { $store, $auth } = useNuxtApp()
const route = useRoute()

defineEmits(['validation'])

$auth.requireLogin()

const { data: referralData } = await useFetch('/api/referrals/')

// State
const submitted = ref(false)
const formErrors = ref([])
const inputErrors = ref({})
const formData = ref({
  email: '',
  code: referralData.value?.referral_code,
  content: referralData.value?.campaign.suggested_email_text,
})

const campaignContent = ref({
  // Default placeholder text
  landing_header_text: 'Invite Your Closest Connoisseurs',
  landing_subheader_text: 'Rack Up $50 Wine Credits',
  landing_blurb_text:
    "When they place their first order of $150 or more, you'll both get a $50 credit.",
  suggested_email_text: '',
  landing_page_cta_button_text: 'Send',
  thank_you_page_header_text: 'Thanks for sharing Wine Access!',
  thank_you_page_blurb_text:
    'Our mission is to connect people and place through wine.',
  ...referralData.value?.campaign,
})

// Computed
const invitePURL = computed(() => {
  return (
    new URL(`${metadata.settingsVars.SEO_HOST}`).host +
    referralData.value?.vanity_url
  )
})
const inviteCode = computed(() => {
  return referralData.value?.referral_code
})

watch(referralData, () => {
  formData.value = {
    code: inviteCode.value,
    content: referralData.value?.campaign.suggested_email_text,
    ...formData.value,
  }
  campaignContent.value = Object.assign(
    campaignContent.value,
    referralData.value?.campaign,
  )
})

// Methods
const copyPURL = async () => {
  pushEvent(
    {
      metric_name: 'Referral PURL Copied',
      url_path: route.fullPath,
    },
    $store,
  )
  await navigator.clipboard.writeText(invitePURL.value)
}

const clearFormErrors = () => {
  formErrors.value = []
  inputErrors.value = {}
}

const submit = async (data, node) => {
  clearFormErrors()
  const { error } = await useFetch('/api/referrals/', {
    method: 'POST',
    body: data,
  })
  if (error.value) {
    console.error(error.value.data)
    if (typeof error.value.data === 'string') {
      node.setErrors([error.value.data])
    } else {
      node.setErrors(error.value.data)
    }
  } else {
    submitted.value = true
  }
}

const resetForm = () => {
  clearFormErrors()
  formData.value = {
    email: '',
    code: inviteCode.value,
    content: campaignContent.value.suggested_email_text,
  }
  submitted.value = false
}
</script>
